import {
  Box,
  CircularProgress,
  Dialog,
  Paper,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import DelayedRedirect from "../../../utils/DelayedRedirect";
import { Transition } from "../../../utils/ModalHelpers";
import { DisplayMessage } from "../../../services/helpers";
import { DriveFleetSourcesRoute } from "../../../constants/routePaths";
import AppBarComponent from "../../Common/Headers/AppBarComponent";
import TextFieldComponent from "../../Common/Inputs/TextFieldComponent";
import ModifiedByInfo from "../../Common/Footers/ModifiedByInfo";
import {
  updateFleetSource,
  createFleetSource,
} from "./../../../services/AssetFleetSources/fleetSourceApisService";
import {
  getFleetSourceById,
  getDriveParties,
} from "../../../services/AssetFleetSources/fleetSourceApisService";
import OutletsDropdown from "./../../Common/Dropdowns/OutletsDropdown";
import DrivePartiesDropdown from "./../../Common/Dropdowns/DrivePartiesDropdown";
import { SettingsRow } from "../../Common/SettingsDispaly/SettingsRow";
import { SettingItem } from "../../Common/SettingsDispaly/SettingItem";

const FleetSourceItem = ({
  resources,
  accessibleMoboOutlets,
  fleetSourceId,
  partyId,
}) => {
  const isEditMode = useMemo(
    () => !!fleetSourceId && fleetSourceId > 0,
    [fleetSourceId]
  );
  const [driveParties, setDriveParties] = useState([]);
  const [redirect, setRedirect] = useState(null);
  const [refreshGrid, setRefreshGrid] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(true);
  const [selectedPartyId, setSelectedPartyId] = useState(partyId ?? 0);
  const [selectedOutlet, setSelectedOutlet] = useState(0);
  const [description, setDescription] = useState("");
  const [requestIsValid, setRequestIsValid] = useState(false);
  const [modifiedBy, setModifiedBy] = useState({
    avatar: "",
    on: "",
    name: "",
  });

  const selectedPartySettings = useMemo(() => {
    if (selectedPartyId > 0 && driveParties.length > 0) {
      return driveParties.find((p) => p.id === selectedPartyId) || null;
    }
    return null;
  }, [selectedPartyId, driveParties]);

  const isRequestValid = useCallback(
    (displayError) => {
      let valid =
        selectedPartyId >= 1 &&
        selectedOutlet > 0 &&
        description &&
        description.trim() !== "";

      if (!valid && displayError) {
        if (selectedPartyId < 1) {
          DisplayMessage(resources["InvalidPartyId"], "error");
        }
        if (!description || description.trim() === "") {
          DisplayMessage(resources["InvalidDescription"], "error");
        }
        if (selectedOutlet < 1) {
          DisplayMessage(resources["InvalidOutlet"], "error");
        }
      }

      setRequestIsValid(valid);
      return valid;
    },
    [
      description,
      resources,
      selectedOutlet,
      selectedPartyId
    ]
  );

  const fetchDriveParties = useCallback(async () => {
    const data = await getDriveParties();
    setDriveParties(data ?? []);
  }, []);

  const fetchFleetSource = useCallback(async () => {
    setRequestInProgress(true);
    const result = await getFleetSourceById(fleetSourceId);
    if (result) {
      setSelectedPartyId(result.party?.id);
      setDescription(result.description);
      setSelectedOutlet(result.outletId || 0);
      setModifiedBy({
        avatar: result.modifiedByAvatar,
        on: result.modifiedOn,
        name: result.modifiedByName,
      });
    } else {
      setRedirect(DriveFleetSourcesRoute);
    }
    setRequestInProgress(false);
  }, [fleetSourceId]);

  useEffect(() => {
    fetchDriveParties();
    if (isEditMode) {
      fetchFleetSource();
    }
  }, [isEditMode, fetchFleetSource, fetchDriveParties]);

  useEffect(() => {
    isRequestValid(false);
  }, [isRequestValid]);

  const handleClose = () => {
    setDialogOpen(false);
    setRedirect(DriveFleetSourcesRoute);
  };

  const handleSubmit = async () => {
    if (!isRequestValid(true)) return;

    setRequestInProgress(true);
    const body = isEditMode
      ? JSON.stringify({
          description,
        })
      : JSON.stringify({
          partyId: selectedPartyId > 0 ? selectedPartyId : null,
          description,
          outletId: selectedOutlet > 0 ? selectedOutlet : null,
        });

    const result = isEditMode
      ? await updateFleetSource(fleetSourceId, body)
      : await createFleetSource(body);

    if (result) {
      setRefreshGrid(true);
      const successMessage = isEditMode
        ? resources["DriveFleetSourceWasUpdatedWithSuccess"]
        : resources["DriveFleetSourceWasCreatedWithSuccess"];
      DisplayMessage(successMessage, "success");
      setRedirect(DriveFleetSourcesRoute);
    }
    setRequestInProgress(false);
  };

  return (
    <Box>
      {redirect && (
        <DelayedRedirect
          to={{ pathname: redirect, state: { refreshGrid: refreshGrid } }}
          delay={250}
        />
      )}
      <Dialog
        fullScreen
        open={dialogOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBarComponent
          idName="driveFleet"
          title={
            isEditMode
              ? resources["EditDriveFleetSource"]
              : resources["NewDriveFleetSource"]
          }
          resources={resources}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          disabled={requestInProgress || !requestIsValid}
        />
        {requestInProgress ? (
          <Box className="centered-loader fadeIn">
            <CircularProgress />
          </Box>
        ) : (
          <Box className="container fadeIn">
            <Box className="py-4 grey">
              <Paper elevation={3} className="p-4 mt-3 mb-3">
                <Box className="d-flex form-group pt-3">
                  <Box className="col-3" />
                  <Box className="col-6">
                    <DrivePartiesDropdown
                      resources={resources}
                      driveParty={selectedPartyId}
                      driveParties={driveParties}
                      onDrivePartyChange={(e) => {
                        const partyId = parseInt(e.target.value);
                        setSelectedPartyId(partyId);
                      }}  
                      required
                      addMarginBottom
                      defaultValue={resources["SelectDriveParty"]}
                      helperText={
                        resources["SelectionOfDrivePartiesIsMandatory"]
                      }
                      disabled={isEditMode}
                      filterFunction={isEditMode ? null : (a) => a.isActive === true}
                    />
                    <OutletsDropdown
                      resources={resources}
                      outlets={accessibleMoboOutlets}
                      outlet={selectedOutlet}
                      onOutletChange={(e) =>
                        setSelectedOutlet(parseInt(e.target.value))
                      }
                      required
                      addMarginBottom
                      defaultValue={resources["SelectOutlet"]}
                      helperText={resources["SelectionOfOutletIsMandatory"]}
                      disabled={isEditMode}
                    />
                    <TextFieldComponent
                      value={description}
                      handleChange={(e) => setDescription(e.target.value)}
                      labelText={resources["Description"]}
                      id="description"
                      maxLength={100}
                      helperText={resources["InputOfDescriptionIsMandatory"]}
                    />
                  </Box>
                  <Box className="col-3" />
                </Box>
                {selectedPartySettings && (
                  <Box className="d-flex form-group pt-2">
                    <Box className="col-3" />
                    <Box className="col-6">
                      <Typography
                        variant="body2"
                        style={{ marginBottom: 8, color: "#999", fontWeight: 500 }}
                      >
                        {resources["PartySettings"] || "Party Settings"}
                      </Typography>

                      <Box>
                        <SettingsRow title={resources["Booking"] || "Booking"}>
                          <SettingItem
                            label={resources["Groups"] || "Groups"}
                            value={selectedPartySettings.bookingSettings?.groups}
                          />
                          <SettingItem
                            label={resources["Classes"] || "Classes"}
                            value={selectedPartySettings.bookingSettings?.classes}
                          />
                          <SettingItem
                            label={resources["Assets"] || "Assets"}
                            value={selectedPartySettings.bookingSettings?.assets}
                          />
                        </SettingsRow>

                        <SettingsRow title={resources["Visibility"] || "Visibility"}>
                          <SettingItem
                            label={resources["Groups"] || "Groups"}
                            value={selectedPartySettings.visibilitySettings?.groups}
                          />
                          <SettingItem
                            label={resources["Classes"] || "Classes"}
                            value={selectedPartySettings.visibilitySettings?.classes}
                          />
                          <SettingItem
                            label={resources["Assets"] || "Assets"}
                            value={selectedPartySettings.visibilitySettings?.assets}
                          />
                        </SettingsRow>

                        <SettingsRow title={resources["Editable"] || "Editable"} maxRows={3}>
                          <SettingItem
                            label={resources["StartDate"] || "Start Date"}
                            value={selectedPartySettings.editableSettings?.isStartDateEditable}
                          />
                          <SettingItem
                            label={resources["EndDate"] || "End Date"}
                            value={selectedPartySettings.editableSettings?.isEndDateEditable}
                          />
                          <SettingItem
                            label={resources["Driver"] || "Driver"}
                            value={selectedPartySettings.editableSettings?.isDriverEditable}
                          />
                          <SettingItem
                            label={resources["Billing"] || "Billing"}
                            value={selectedPartySettings.editableSettings?.isBillingEditable}
                          />
                          <SettingItem
                            label={resources["RequestedType"] || "Requested Type"}
                            value={selectedPartySettings.editableSettings?.isRequestedTypeEditable}
                          />
                          <SettingItem
                            label={resources["Comment"] || "Comment"}
                            value={selectedPartySettings.editableSettings?.isCommentEditable}
                          />
                          <SettingItem
                            label={resources["CheckIn"] || "Check In"}
                            value={selectedPartySettings.editableSettings?.canCheckIn}
                          />
                          <SettingItem
                            label={resources["CheckOut"] || "Check Out"}
                            value={selectedPartySettings.editableSettings?.canCheckOut}
                          />
                        </SettingsRow>
                      </Box>
                    </Box>
                    <Box className="col-3" />
                  </Box>
                )}
                {isEditMode && (
                  <ModifiedByInfo
                    resources={resources}
                    modifiedByAvatar={modifiedBy.avatar}
                    modifiedOn={modifiedBy.on}
                    modifiedByName={modifiedBy.name}
                    className="mt-3"
                  />
                )}
              </Paper>
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default FleetSourceItem;
